import { Injectable } from '@angular/core';
import { ConfigService } from 'providers/config.service';
import { AuthService } from 'providers/auth.service';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class TariffsApiService {
    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private config: ConfigService
    ) {}

    /**
     * Create new or update existing tariff
     */
    async putTariff(tariffId: string, body: PutTariffRequest): Promise<PutTariffResponse> {
        return this.http
            .put<PutTariffResponse>(`${this.config.restApiUrl}/tariffs/${encodeURIComponent(tariffId)}`, body, {
                headers: {
                    Authorization: `Bearer ${this.authService.jwt}`,
                },
            })
            .toPromise();
    }

    /**
     * Get tariffs
     */
    async getTariffs(queryParams?: GetTariffsQueryParameters): Promise<GetTariffsResponse> {
        return this.http
            .get<GetTariffsResponse>(
                `${this.config.restApiUrl}/tariffs?${Object.keys(queryParams || {})
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Delete tariff
     */
    async deleteTariff(tariffId: string): Promise<DeleteTariffResponse> {
        return this.http
            .delete<DeleteTariffResponse>(`${this.config.restApiUrl}/tariffs/${encodeURIComponent(tariffId)}`, {
                headers: {
                    Authorization: `Bearer ${this.authService.jwt}`,
                },
            })
            .toPromise();
    }

    /**
     * Get enabled tariffIds for station
     */
    async getStationTariffs(stationId: string): Promise<GetStationTariffsResponse> {
        return this.http
            .get<GetStationTariffsResponse>(
                `${this.config.restApiUrl}/tariffs/station/${encodeURIComponent(stationId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Set enabled tariffIds for station
     */
    async putStationTariffs(stationId: string, body: PutStationTariffsRequest): Promise<PutStationTariffsResponse> {
        return this.http
            .put<PutStationTariffsResponse>(
                `${this.config.restApiUrl}/tariffs/station/${encodeURIComponent(stationId)}`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }
}

export interface DeleteTariffResponse {
    success: boolean;
}

export interface GetStationTariffsResponse {
    tariffIds: string[];
}

export interface GetTariffsQueryParameters {
    date_from?: string;
    date_to?: string;
    offset?: number;
    limit?: number;
}

export interface GetTariffsResponse {
    /**
     * NOTE this response doesn't follow OCPP 2.2 standard
     */
    tariffs: TariffBase[];
}

export interface PutStationTariffsRequest {
    tariffIds: string[];
}

export interface PutStationTariffsResponse {
    success: boolean;
}

export interface PutTariffRequest {
    name?: string;
    description?: string;
    /**
     * OCPI 2.2 tariff
     */
    tariff: {
        /**
         * ISO-3166 alpha-2 country code of the CPO that owns this Tariff
         */
        country_code: string;
        /**
         * CPO ID of the CPO that own this Tariff (following the ISO-15118 standard)
         */
        party_id: string;
        /**
         * Uniquely identifies the tariff within the CPO's platform (and suboperator platforms)
         */
        id: string;
        /**
         * ISO-4217 code of the currency of this tariff
         */
        currency: string;
        /**
         * Defines the type of the tariff. This allows for distinction in case of given
         * Charging Preferences. When omitted, this tariff is valid for all sessions
         */
        type?: 'AD_HOC_PAYMENT' | 'PROFILE_CHEAP' | 'PROFILE_FAST' | 'PROFILE_GREEN' | 'REGULAR';
        /**
         * List of multi-language alternative tariff info texts
         */
        tariff_alt_text?: DisplayText[];
        /**
         * URL to a web page that contains an explanation of the tariff information in human readable form
         */
        tariff_alt_url?: string;
        /**
         * When this field is set, a Charging Session with this tariff will at least cost this amount.
         * This is different from FLAT fee (Start Tariff, Transaction Fee), as a FLAT fee is a fixed
         * amount that has to be paid for any Charging Session. A minimum price indicates that when
         * the cost of a Charging Session is lower than this amount, the cost of the Session will be
         * equal to this amount. See OCPI 2.2-d2 section 11.3.1 for more information
         */
        min_price?: {
            /**
             * Price/Cost excluding VAT
             */
            excl_vat: number;
            /**
             * Price/Cost including VAT
             */
            incl_vat?: number;
        };
        /**
         * When this field is set, a Charging Session with this tariff will NOT cost more than this amount.
         * See OCPI 2.2-d2 section 11.3.1 for more information
         */
        max_price?: {
            /**
             * Price/Cost excluding VAT
             */
            excl_vat: number;
            /**
             * Price/Cost including VAT
             */
            incl_vat?: number;
        };
        /**
         * List of Tariff Elements
         */
        elements: TariffElement[];
        /**
         * RFC 3339 UTC timestamp
         * The time when this tariff becomes active, in UTC, time_zone field of the Location can
         * be used to convert to local time. Typically used for a new tariff that is already given
         * with the location, before it becomes active. See OCPI 2.2-d2 section 11.3.1 for more information
         */
        start_date_time?: string;
        /**
         * RFC 3339 UTC timestamp
         * The time after which this tariff is no longer ali, in UTC, time_zone field of the Location
         * can be used to conver to local time. Typically used when this tariff is going to be replaced
         * with a different tariff in the near future. See OCPI 2.2-d2 section 11.3.1 for more information
         */
        end_date_time?: string;
        /**
         * Details on the energy supplied with this tariff
         */
        energy_mix?: {
            /**
             * True if 100% regenerative sources. (CO2 and nuclear waste is zero)
             */
            is_green_energy: boolean;
            /**
             * Key-value pairs (enum + percentage) of energy sources of this location's tariff
             */
            energy_sources?: EnergySource[];
            /**
             * Key-value pairs (enum + percentage) of nuclear waste and CO2 exhaust of this location's tariff
             */
            environ_impact?: EnvironmentalImpact[];
            /**
             * Name of the energy supplier, delivering the energy for this location or tariff.
             * (this field can be used to look-up energy qualification or to show it directly to the customer
             * (for well-known brands like Greenpeace Energy, etc.))
             */
            supplier_name?: string;
            /**
             * Name of the energy suppliers product/tariff plan used at this location.
             * (this field can be used to look-up energy qualification or to show it directly to the customer
             * (for well-known brands like Greenpeace Energy, etc.))
             */
            energy_product_name?: string;
        };
        /**
         * RFC 3339 UTC timestamp
         * Timestamp when this Tariff was last updated (or created)
         */
        last_updated?: string;
    };
}

export interface PutTariffResponse {
    success: boolean;
}

export interface TariffBase {
    name?: string;
    description?: string;
    /**
     * OCPI 2.2 tariff
     */
    tariff: {
        /**
         * ISO-3166 alpha-2 country code of the CPO that owns this Tariff
         */
        country_code: string;
        /**
         * CPO ID of the CPO that own this Tariff (following the ISO-15118 standard)
         */
        party_id: string;
        /**
         * Uniquely identifies the tariff within the CPO's platform (and suboperator platforms)
         */
        id: string;
        /**
         * ISO-4217 code of the currency of this tariff
         */
        currency: string;
        /**
         * Defines the type of the tariff. This allows for distinction in case of given
         * Charging Preferences. When omitted, this tariff is valid for all sessions
         */
        type?: 'AD_HOC_PAYMENT' | 'PROFILE_CHEAP' | 'PROFILE_FAST' | 'PROFILE_GREEN' | 'REGULAR';
        /**
         * List of multi-language alternative tariff info texts
         */
        tariff_alt_text?: DisplayText[];
        /**
         * URL to a web page that contains an explanation of the tariff information in human readable form
         */
        tariff_alt_url?: string;
        /**
         * When this field is set, a Charging Session with this tariff will at least cost this amount.
         * This is different from FLAT fee (Start Tariff, Transaction Fee), as a FLAT fee is a fixed
         * amount that has to be paid for any Charging Session. A minimum price indicates that when
         * the cost of a Charging Session is lower than this amount, the cost of the Session will be
         * equal to this amount. See OCPI 2.2-d2 section 11.3.1 for more information
         */
        min_price?: {
            /**
             * Price/Cost excluding VAT
             */
            excl_vat: number;
            /**
             * Price/Cost including VAT
             */
            incl_vat?: number;
        };
        /**
         * When this field is set, a Charging Session with this tariff will NOT cost more than this amount.
         * See OCPI 2.2-d2 section 11.3.1 for more information
         */
        max_price?: {
            /**
             * Price/Cost excluding VAT
             */
            excl_vat: number;
            /**
             * Price/Cost including VAT
             */
            incl_vat?: number;
        };
        /**
         * List of Tariff Elements
         */
        elements: TariffElement[];
        /**
         * RFC 3339 UTC timestamp
         * The time when this tariff becomes active, in UTC, time_zone field of the Location can
         * be used to convert to local time. Typically used for a new tariff that is already given
         * with the location, before it becomes active. See OCPI 2.2-d2 section 11.3.1 for more information
         */
        start_date_time?: string;
        /**
         * RFC 3339 UTC timestamp
         * The time after which this tariff is no longer ali, in UTC, time_zone field of the Location
         * can be used to conver to local time. Typically used when this tariff is going to be replaced
         * with a different tariff in the near future. See OCPI 2.2-d2 section 11.3.1 for more information
         */
        end_date_time?: string;
        /**
         * Details on the energy supplied with this tariff
         */
        energy_mix?: {
            /**
             * True if 100% regenerative sources. (CO2 and nuclear waste is zero)
             */
            is_green_energy: boolean;
            /**
             * Key-value pairs (enum + percentage) of energy sources of this location's tariff
             */
            energy_sources?: EnergySource[];
            /**
             * Key-value pairs (enum + percentage) of nuclear waste and CO2 exhaust of this location's tariff
             */
            environ_impact?: EnvironmentalImpact[];
            /**
             * Name of the energy supplier, delivering the energy for this location or tariff.
             * (this field can be used to look-up energy qualification or to show it directly to the customer
             * (for well-known brands like Greenpeace Energy, etc.))
             */
            supplier_name?: string;
            /**
             * Name of the energy suppliers product/tariff plan used at this location.
             * (this field can be used to look-up energy qualification or to show it directly to the customer
             * (for well-known brands like Greenpeace Energy, etc.))
             */
            energy_product_name?: string;
        };
        /**
         * RFC 3339 UTC timestamp
         * Timestamp when this Tariff was last updated (or created)
         */
        last_updated?: string;
    };
}

export interface Tariff {
    /**
     * ISO-3166 alpha-2 country code of the CPO that owns this Tariff
     */
    country_code: string;
    /**
     * CPO ID of the CPO that own this Tariff (following the ISO-15118 standard)
     */
    party_id: string;
    /**
     * Uniquely identifies the tariff within the CPO's platform (and suboperator platforms)
     */
    id: string;
    /**
     * ISO-4217 code of the currency of this tariff
     */
    currency: string;
    /**
     * Defines the type of the tariff. This allows for distinction in case of given
     * Charging Preferences. When omitted, this tariff is valid for all sessions
     */
    type?: 'AD_HOC_PAYMENT' | 'PROFILE_CHEAP' | 'PROFILE_FAST' | 'PROFILE_GREEN' | 'REGULAR';
    /**
     * List of multi-language alternative tariff info texts
     */
    tariff_alt_text?: DisplayText[];
    /**
     * URL to a web page that contains an explanation of the tariff information in human readable form
     */
    tariff_alt_url?: string;
    /**
     * When this field is set, a Charging Session with this tariff will at least cost this amount.
     * This is different from FLAT fee (Start Tariff, Transaction Fee), as a FLAT fee is a fixed
     * amount that has to be paid for any Charging Session. A minimum price indicates that when
     * the cost of a Charging Session is lower than this amount, the cost of the Session will be
     * equal to this amount. See OCPI 2.2-d2 section 11.3.1 for more information
     */
    min_price?: {
        /**
         * Price/Cost excluding VAT
         */
        excl_vat: number;
        /**
         * Price/Cost including VAT
         */
        incl_vat?: number;
    };
    /**
     * When this field is set, a Charging Session with this tariff will NOT cost more than this amount.
     * See OCPI 2.2-d2 section 11.3.1 for more information
     */
    max_price?: {
        /**
         * Price/Cost excluding VAT
         */
        excl_vat: number;
        /**
         * Price/Cost including VAT
         */
        incl_vat?: number;
    };
    /**
     * List of Tariff Elements
     */
    elements: TariffElement[];
    /**
     * RFC 3339 UTC timestamp
     * The time when this tariff becomes active, in UTC, time_zone field of the Location can
     * be used to convert to local time. Typically used for a new tariff that is already given
     * with the location, before it becomes active. See OCPI 2.2-d2 section 11.3.1 for more information
     */
    start_date_time?: string;
    /**
     * RFC 3339 UTC timestamp
     * The time after which this tariff is no longer ali, in UTC, time_zone field of the Location
     * can be used to conver to local time. Typically used when this tariff is going to be replaced
     * with a different tariff in the near future. See OCPI 2.2-d2 section 11.3.1 for more information
     */
    end_date_time?: string;
    /**
     * Details on the energy supplied with this tariff
     */
    energy_mix?: {
        /**
         * True if 100% regenerative sources. (CO2 and nuclear waste is zero)
         */
        is_green_energy: boolean;
        /**
         * Key-value pairs (enum + percentage) of energy sources of this location's tariff
         */
        energy_sources?: EnergySource[];
        /**
         * Key-value pairs (enum + percentage) of nuclear waste and CO2 exhaust of this location's tariff
         */
        environ_impact?: EnvironmentalImpact[];
        /**
         * Name of the energy supplier, delivering the energy for this location or tariff.
         * (this field can be used to look-up energy qualification or to show it directly to the customer
         * (for well-known brands like Greenpeace Energy, etc.))
         */
        supplier_name?: string;
        /**
         * Name of the energy suppliers product/tariff plan used at this location.
         * (this field can be used to look-up energy qualification or to show it directly to the customer
         * (for well-known brands like Greenpeace Energy, etc.))
         */
        energy_product_name?: string;
    };
    /**
     * RFC 3339 UTC timestamp
     * Timestamp when this Tariff was last updated (or created)
     */
    last_updated?: string;
}

export type TariffType = 'AD_HOC_PAYMENT' | 'PROFILE_CHEAP' | 'PROFILE_FAST' | 'PROFILE_GREEN' | 'REGULAR';

export interface DisplayText {
    /**
     * Language Code ISO 639-1
     */
    language: string;
    /**
     * Text to be displayed to a end user. No markup, html etc. allowed
     */
    text: string;
}

export interface Price {
    /**
     * Price/Cost excluding VAT
     */
    excl_vat: number;
    /**
     * Price/Cost including VAT
     */
    incl_vat?: number;
}

export interface TariffElement {
    /**
     * List of price components that describe the pricing of a tariff
     */
    price_components: PriceComponent[];
    restrictions?: TariffRestrictions;
}

export interface PriceComponent {
    /**
     * Type of tariff dimension
     */
    type: 'ENERGY' | 'FLAT' | 'PARKING_TIME' | 'TIME';
    /**
     * Price per unit (excl. VAT) for this tariff dimension
     */
    price: number;
    /**
     * Applicable VAT percentage for this tariff dimension.
     * If omitted, no VAT is applicable. Not providing a VAT is different from 0% VAT,
     * which would be a value of 0.0 here
     */
    vat?: number;
    /**
     * Minimum amount to be billed. This unit will be billed in this step_size blocks.
     * Amounts that are less then this step_size are rounded up to the given step_size.
     * For example: if type is TIME and step_size has a value of 300, then time will be billed in blocks
     * of 5 minutes. If 6 minutes were used, 10 minutes (2 blocks of step_size) will be billed.
     * See OCPI 2.2-d2 section 11.4.2 for more information
     */
    step_size: number;
}

/**
 * ENERGY - Defined in kWh, step_size multiplier: 1Wh
 * FLAT - Flat fee without unit for step_size
 * PARKING_TIME - Time not charging: defined in hours, step_size multiplier: 1 second
 * TIME - Time charging: defined in hours, step_size multiplier: 1 second. Can also be used in
 * combination with a RESERVATION restriction to describe the price of the reservation time
 */
export type TariffDimensionType = 'ENERGY' | 'FLAT' | 'PARKING_TIME' | 'TIME';

/**
 * These restrictions are not for the entire Charging Session. They only describe if and when a TariffElement
 * becomes active or inactive during a Charging Session.
 */
export interface TariffRestrictions {
    /**
     * Start time of day in locla time, the time zone is defined in the time_zone field of the Location,
     * for example 13:30, valid from this time of the day. Must be in 24h format with leading zeros.
     * Hour/Minute separator: ':'  Regex: ([0-1][0-9]|2[0-3]):[0-5][0-9]
     */
    start_time?: string;
    /**
     * End time of day in local time, the time zone is defined in the time_zone field of
     * the Location, for example 19:45, valid until this time of the day. Same syntax as
     * start_time. If end_time < start_time then the period wraps around to the next
     * day. To stop at end of the day use: 00:00.
     */
    end_time?: string;
    /**
     * Start date in local time, the time zone is defined in the time_zone field of the Location,
     * for example: 2015-12-24, valid from this day (inclusive).
     * Regex: ([12][0-9]{3})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])
     */
    start_date?: string;
    /**
     * End date in local time, the time zone is defined in the time_zone field of the Location,
     * for example: 2015-12-27, valid until this day (exclusive). Same syntax as start_date
     */
    end_date?: string;
    /**
     * Minimum consumed energy in kWh, for example 20, valid from this amount of energy (inclusive) being used
     */
    min_kwh?: number;
    /**
     * Maximum consumed energy in kWh, for example 50, valid from this amount of energy (exclusive) being used
     */
    max_kwh?: number;
    /**
     * Sum of the minimum current (in Amperes) over all phases, for example 5. When the EV is charging with more
     * than, or equal to, the defined amount of current, this TariffElement is/becomes active. If the charging
     * current is or becomes lower, this TariffElement is not or no longer valid and becomes inactive. This describes
     * NOT the minimum current over the entire Charging Session. This restriction can make a TariffElement become
     * active when the charging current is above the defined value, but the TariffElement MUSTno longer be active
     * when the charging current drops below the defined value.
     */
    min_current?: number;
    /**
     * Sum of the maximum current (in Amperes) over all phases, for example 20. When the EV is charging with less
     * than the defined amount of current, this TariffElement becomes/is active. If the charging current is or
     * becomes higher, this TariffElement is not or no longer valid and becomes inactive. This describes NOT the
     * maximum current over the entire Charging Session. This restriction can make a TariffElement become active
     * when the charging current is below this value, but the TariffElement MUST no longer be active when the
     * charging current raises above the defined value.
     */
    max_current?: number;
    /**
     * Minimum power in kW, for example 5. When the EV is charging with mote than, or equal to, the defined
     * amount of power, this TariffElement is/becomes active. If the charging power is or becomes lower, this
     * TariffElement is not or no longer valid and becomes inactive. This describes NOT the minimum power
     * over the entire Charging Session. This restriction can make a TariffElement become active when the
     * charging power is above this value, but the TariffElement MUST no longer be active when the
     * charging power drops below the defined value.
     */
    min_power?: number;
    /**
     * Maximum power in kW, for example 20. When the EV is charging with less than the defined amount of power,
     * this TariffElement becomes/is active. If the charging power is or becomes higher, this TariffElement is
     * not or no longer valid and becomes inactive. This describes NOT the maximum power over the entire
     * Charging Session. This restriction can make a TariffElement become active when the charging power
     * is below this value, but the TariffElement MUST no longer be active when the
     * charging power raises above the defined value
     */
    max_power?: number;
    /**
     * Minimum duration in seconds the Charging Session MUST last (inclusive). When the duration of a Charging
     * Session is longer than than the defined value, this TariffElement is or becomes active.
     * Before that moment, this TariffElement is not yet active.
     */
    min_duration?: number;
    /**
     * Maximum duration in seconds the Charging Session MUST last (exclusive). When the duration of a
     * Charging Session is shorter than the defined value, this TariffElement is or becomes active.
     * After that moment, this TariffElement is no longer active.
     */
    max_duration?: number;
    /**
     * Which day(s) of the week this TariffElement is active.
     */
    day_of_week?: ('FRIDAY' | 'MONDAY' | 'SATURDAY' | 'SUNDAY' | 'THURSDAY' | 'TUESDAY' | 'WEDNESDAY')[];
    /**
     * When this field is present, the TariffElement describes reservation costs. A reservation
     * starts when the reservation is made, and end when the driver starts charging on the reserved
     * EVSE/Location, or when the reservation expires. A reservation can only have: FLAT and TIME
     * TariffDimensions, where TIME is for the duration of the reservation
     */
    reservation?: 'RESERVATION' | 'RESERVATION_EXPIRES';
}

/**
 * RESERVATION - Used in TariffElements to describe costs for a reservation
 * RESERVATION_EXPIRES - Used in TariffElements to describe costs for a reservation that expires (i.e. driver
 * does not start a charging session before expiry_date of the reservation)
 */
export type ReservationRestrictionType = 'RESERVATION' | 'RESERVATION_EXPIRES';

/**
 * This type is used to specify the energy mix and environmental impact of the supplied energy at a location or in a tariff.
 */
export interface EnergyMix {
    /**
     * True if 100% regenerative sources. (CO2 and nuclear waste is zero)
     */
    is_green_energy: boolean;
    /**
     * Key-value pairs (enum + percentage) of energy sources of this location's tariff
     */
    energy_sources?: EnergySource[];
    /**
     * Key-value pairs (enum + percentage) of nuclear waste and CO2 exhaust of this location's tariff
     */
    environ_impact?: EnvironmentalImpact[];
    /**
     * Name of the energy supplier, delivering the energy for this location or tariff.
     * (this field can be used to look-up energy qualification or to show it directly to the customer
     * (for well-known brands like Greenpeace Energy, etc.))
     */
    supplier_name?: string;
    /**
     * Name of the energy suppliers product/tariff plan used at this location.
     * (this field can be used to look-up energy qualification or to show it directly to the customer
     * (for well-known brands like Greenpeace Energy, etc.))
     */
    energy_product_name?: string;
}

/**
 * Key-value pairs (enum + percentage) of energy sources. All given values of all categories should add up to 100 percent.
 */
export interface EnergySource {
    /**
     * The type of energy source
     */
    source: 'COAL' | 'GAS' | 'GENERAL_FOSSIL' | 'GENERAL_GREEN' | 'NUCLEAR' | 'SOLAR' | 'WATER' | 'WIND';
    /**
     * Percentage of this source (0-100) in the mix
     */
    percentage: number;
}

/**
 * Categories of energy sources.
 *
 * NUCLEAR - Nuclear power sources.
 * GENERAL_FOSSIL - All kinds of fossil power sources.
 * COAL - Fossil power from coal.
 * GAS - Fossil power from gas.
 * GENERAL_GREEN - All kinds of regenerative power sources.
 * SOLAR - Regenerative power from PV.
 * WIND - Regenerative power from wind turbines.
 * WATER - Regenerative power from water turbines.
 */
export type EnergySourceCategory =
    | 'COAL'
    | 'GAS'
    | 'GENERAL_FOSSIL'
    | 'GENERAL_GREEN'
    | 'NUCLEAR'
    | 'SOLAR'
    | 'WATER'
    | 'WIND';

/**
 * Amount of waste produced/emitted per kWh
 */
export interface EnvironmentalImpact {
    /**
     * The environmental impact category of this value.
     */
    category: 'CARBON_DIOXIDE' | 'NUCLEAR_WASTE';
    /**
     * Amount of this portion in g/kWh
     */
    amount: number;
}

/**
 * Categories of environmental impact values.
 *
 * NUCLEAR_WASTE - Produced nuclear waste in grams per kWh
 * CARBON_DIOXIDE - Exhausted carbon dioxide in grams per kWh
 */
export type EnvironmentalImpactCategory = 'CARBON_DIOXIDE' | 'NUCLEAR_WASTE';
